import SliderSelect from '@src/components/common/PlainSlider';
import ZoomPlusIcon from '@src/svgs/ZoomPlusIcon';
import {
  getUnifiedValue,
  resolveElementFromPath,
} from '@src/utility/compositionSettings';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ImagePreset } from '../../ImagePreset';
import { ControlFieldLabel } from './ControlField';

import type {
  ControlConfiguration,
  ControlRenderProps,
  HeadshotControlConfiguration,
  ImageControlConfiguration,
  LogoControlConfiguration,
} from '../types';
import type { ElementState } from '@src/renderer/ElementState';
import type { ElementLike } from '@src/utility/compositionSettings';
// ImageControl: renders an image preset selector and delegates source changes upward via modifyProperty.
export const ImageControl = observer(
  ({
    control,
    activeElement,
    modifyProperty,
    compositionMeta,
    onScaleChange,
  }: ControlRenderProps<
    | ImageControlConfiguration
    | LogoControlConfiguration
    | HeadshotControlConfiguration
  > & {
    onScaleChange?: (control: ControlConfiguration, value: unknown) => void;
  }) => {
    const imageElement = resolveElementFromPath(
      activeElement as unknown as ElementLike,
      control.elementPaths[0] || [],
    );
    const { unifiedValue } = getUnifiedValue(
      control,
      activeElement,
      compositionMeta,
    );

    const initialUrl = typeof unifiedValue === 'string' ? unifiedValue : '';

    const onImageSourceChange = (newUrl: string) => {
      modifyProperty(control, newUrl);
    };

    if (!imageElement) return null;

    return (
      <Container>
        <ControlContainer fullWidth={!onScaleChange}>
          <ControlFieldLabel>{control.label}</ControlFieldLabel>
          <ImagePreset
            activeElement={imageElement as unknown as ElementState}
            parentCompositionElement={activeElement}
            url={initialUrl || ''}
            onSourceChange={onImageSourceChange}
            isImageControl={control.type === 'image'}
            isLogoControl={control.type === 'logo'}
            isHeadshotControl={
              control.type === 'headshot' || /headshot/i.test(control.label)
            }
          />
        </ControlContainer>
        {!!onScaleChange && (
          <ZoomContainer>
            <ControlFieldLabel>Zoom</ControlFieldLabel>
            <SliderAndIcon>
              <ZoomPlusIcon strokeColor="var(--accent)" />
              <SliderSelect
                getValue={(scale: number) => onScaleChange(control, scale)}
                defaultValue={100}
                currValue={parseFloat(activeElement.source.x_scale) || 100}
                min={100}
                max={150}
              />
            </SliderAndIcon>
          </ZoomContainer>
        )}
      </Container>
    );
  },
);

export default ImageControl;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing-xs);
`;

const ControlContainer = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: auto;
`;

const SliderAndIcon = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px;
  gap: var(--spacing-xs);
  align-items: center;
  height: 15px;
  width: 100%;
`;

const ZoomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  gap: var(--spacing-xs);
  width: 100%;
`;
